import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { listReviewItems, reviewAction, listSignals } from '../api/client';
import { ConfidenceIndicator, EmptyState } from '../components';
import type { Signal } from '../components';

interface ReviewItem {
  id: string;
  signal_id: string;
  reason: string;
  confidence: number;
  created_at: string;
  resolved: boolean;
  signal?: Signal;
}

const URGENCY_LEVELS = ['critical', 'high', 'medium', 'low'] as const;

function relativeTime(dateStr: string): string {
  const mins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function Review() {
  const { t } = useTranslation();
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ summary: '', category: '', urgency: '' });
  const [mergeId, setMergeId] = useState<string | null>(null);
  const [mergeTargetId, setMergeTargetId] = useState('');
  const [allSignals, setAllSignals] = useState<Signal[]>([]);
  const [urgencyDropdownId, setUrgencyDropdownId] = useState<string | null>(null);
  const [categoryDropdownId, setCategoryDropdownId] = useState<string | null>(null);

  const fetchItems = useCallback(async () => {
    try {
      const res = await listReviewItems({ resolved: false });
      setItems(res.data?.items ?? res.data ?? []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleAction = async (itemId: string, action: string, data?: Record<string, unknown>) => {
    await reviewAction(itemId, { action, ...data });
    fetchItems();
  };

  const startEdit = (item: ReviewItem) => {
    setEditingId(item.id);
    setEditForm({
      summary: item.signal?.summary ?? '',
      category: item.signal?.category ?? '',
      urgency: item.signal?.urgency ?? '',
    });
  };

  const submitEdit = async () => {
    if (!editingId) return;
    await reviewAction(editingId, { action: 'edit', updates: editForm });
    setEditingId(null);
    fetchItems();
  };

  const startMerge = async (itemId: string) => {
    setMergeId(itemId);
    if (allSignals.length === 0) {
      try {
        const res = await listSignals();
        setAllSignals(res.data?.signals ?? res.data ?? []);
      } catch { /* empty */ }
    }
  };

  const submitMerge = async () => {
    if (!mergeId || !mergeTargetId) return;
    await reviewAction(mergeId, { action: 'merge', merge_with_signal_id: mergeTargetId });
    setMergeId(null);
    setMergeTargetId('');
    fetchItems();
  };

  const reasonColors: Record<string, string> = {
    low_confidence: 'bg-red-100 text-red-700',
    unclear_ownership: 'bg-yellow-100 text-yellow-700',
    missing_due_date: 'bg-orange-100 text-orange-700',
    possible_duplicate: 'bg-purple-100 text-purple-700',
    ambiguous_intent: 'bg-blue-100 text-blue-700',
  };

  const CATEGORIES = ['customer_order', 'order_change', 'delivery_update', 'pickup_request', 'supplier_issue', 'inventory_shortage', 'production_task', 'roasting_request', 'packaging_request', 'invoice_or_payment', 'followup_needed', 'reminder', 'urgent_issue', 'general_information', 'noise'] as const;

  if (loading) {
    return <div className="flex items-center justify-center min-h-[40vh] text-gray-400">{t('common.loading')}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 inline-flex items-center gap-3">
            {t('review.title')}
            {items.length > 0 && (
              <span className="inline-flex items-center justify-center rounded-full bg-orange-500 text-white text-xs font-bold min-w-[22px] h-[22px] px-1.5">
                {items.length}
              </span>
            )}
          </h1>
          <p className="text-sm text-gray-500 mt-1">{t('review.subtitle')}</p>
        </div>
      </div>

      {items.length === 0 ? (
        <EmptyState icon="✅" title={t('review.title')} description={t('review.emptyState')} />
      ) : (
        <div className="space-y-4">
          {items.map((item) => (
            <div key={item.id} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm hover:shadow-md transition-shadow">
              {/* Signal info */}
              {item.signal && (
                <div className="mb-3">
                  <p className="text-sm font-medium text-gray-900 line-clamp-2">{item.signal.summary}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-gray-500">
                    {item.signal.source_chat_name && <span>💬 {item.signal.source_chat_name}</span>}
                    {item.signal.category && (
                      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                        {t(`categories.${item.signal.category}`, item.signal.category)}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Review reason + confidence + timestamp */}
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${reasonColors[item.reason] ?? 'bg-gray-100 text-gray-600'}`}>
                  {t(`review.reasons.${item.reason}`, item.reason)}
                </span>
                <div className="w-28">
                  <ConfidenceIndicator confidence={item.confidence} />
                </div>
                <span className="text-xs text-gray-400">{relativeTime(item.created_at)}</span>
              </div>

              {/* Inline edit form */}
              {editingId === item.id ? (
                <div className="space-y-2 mb-3 p-3 bg-gray-50 rounded-md">
                  <input value={editForm.summary} onChange={(e) => setEditForm({ ...editForm, summary: e.target.value })} className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm" placeholder="Summary" />
                  <div className="grid grid-cols-2 gap-2">
                    <select value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} className="rounded-md border border-gray-300 px-2 py-1.5 text-sm">
                      <option value="">Category</option>
                      {CATEGORIES.map((c) => <option key={c} value={c}>{t(`categories.${c}`)}</option>)}
                    </select>
                    <select value={editForm.urgency} onChange={(e) => setEditForm({ ...editForm, urgency: e.target.value })} className="rounded-md border border-gray-300 px-2 py-1.5 text-sm">
                      <option value="">Urgency</option>
                      {URGENCY_LEVELS.map((u) => <option key={u} value={u}>{t(`urgency.${u}`)}</option>)}
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={submitEdit} className="rounded-md bg-primary-600 px-3 py-1 text-xs font-medium text-white hover:bg-primary-700">{t('common.save')}</button>
                    <button onClick={() => setEditingId(null)} className="rounded-md px-3 py-1 text-xs text-gray-600 hover:bg-gray-200">{t('common.cancel')}</button>
                  </div>
                </div>
              ) : null}

              {/* Action buttons */}
              <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
                <button onClick={() => handleAction(item.id, 'approve')} className="inline-flex items-center gap-1 rounded-md bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700 hover:bg-green-100 transition-colors">
                  ✓ {t('review.approve')}
                </button>
                <button onClick={() => startEdit(item)} className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 transition-colors">
                  ✏️ {t('common.edit')}
                </button>
                <button onClick={() => handleAction(item.id, 'dismiss')} className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-100 transition-colors">
                  ✗ {t('common.dismiss')}
                </button>
                <button onClick={() => startMerge(item.id)} className="inline-flex items-center gap-1 rounded-md bg-purple-50 px-2.5 py-1 text-xs font-medium text-purple-700 hover:bg-purple-100 transition-colors">
                  🔀 {t('common.merge')}
                </button>

                {/* Assign Category dropdown */}
                <div className="relative">
                  <button onClick={() => setCategoryDropdownId(categoryDropdownId === item.id ? null : item.id)} className="inline-flex items-center gap-1 rounded-md bg-yellow-50 px-2.5 py-1 text-xs font-medium text-yellow-700 hover:bg-yellow-100 transition-colors">
                    {t('review.assignCategory')}
                  </button>
                  {categoryDropdownId === item.id && (
                    <div className="absolute top-full left-0 mt-1 z-10 bg-white border border-gray-200 rounded-md shadow-lg py-1 max-h-48 overflow-y-auto min-w-[180px]">
                      {CATEGORIES.map((c) => (
                        <button key={c} onClick={() => { handleAction(item.id, 'assign_category', { category: c }); setCategoryDropdownId(null); }} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50">
                          {t(`categories.${c}`)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Set Urgency dropdown */}
                <div className="relative">
                  <button onClick={() => setUrgencyDropdownId(urgencyDropdownId === item.id ? null : item.id)} className="inline-flex items-center gap-1 rounded-md bg-orange-50 px-2.5 py-1 text-xs font-medium text-orange-700 hover:bg-orange-100 transition-colors">
                    {t('review.setUrgency')}
                  </button>
                  {urgencyDropdownId === item.id && (
                    <div className="absolute top-full left-0 mt-1 z-10 bg-white border border-gray-200 rounded-md shadow-lg py-1 min-w-[120px]">
                      {URGENCY_LEVELS.map((u) => (
                        <button key={u} onClick={() => { handleAction(item.id, 'set_urgency', { urgency: u }); setUrgencyDropdownId(null); }} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50">
                          {t(`urgency.${u}`)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Merge modal */}
      {mergeId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setMergeId(null)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">🔀 {t('common.merge')}</h3>
            <p className="text-sm text-gray-500 mb-3">Select a signal to merge with:</p>
            <select value={mergeTargetId} onChange={(e) => setMergeTargetId(e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm mb-4">
              <option value="">Select signal...</option>
              {allSignals.map((s) => (
                <option key={s.id} value={s.id}>{s.summary.substring(0, 80)}</option>
              ))}
            </select>
            <div className="flex justify-end gap-2">
              <button onClick={() => setMergeId(null)} className="rounded-md px-4 py-2 text-sm text-gray-600 hover:bg-gray-100">{t('common.cancel')}</button>
              <button onClick={submitMerge} disabled={!mergeTargetId} className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">{t('common.merge')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
